import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import EditIcon from '@material-ui/icons/Edit';
import BarChartIcon from '@material-ui/icons/BarChart';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import { SOLUTION } from './solutions';
import { FEATURE_KEY, USER_ACCESS_LEVEL, LEARNCREATOR_START_PATH } from '../../utils/constants';
import { isFeatureEnabled } from '../../utils/productAccess';
import { COS_PREVIEW_PATH } from '../../utils/cpci.util';

/**
 * School Workspace navigation (level 22).
 *
 * Structure and labels come from the PbDQ Navigation Bar sheet, rows 105-147.
 * Routes and gates are carried over unchanged from the hand-written sidebar this
 * replaces — the sheet restructures the menu, it does not grant new access.
 *
 * Row shape is the same as partnerWorkspace.nav.js; see that file for the field
 * reference. `ctx` here is
 *   { authUser, insightsPath, hasAnyProgramAccess, hasLearningPrograms,
 *     canViewLearningPrograms, canCreateLearningPrograms }
 * — the same values PartialMainList already computed.
 *
 * DIFFERENCES FROM PARTNER, all from the sheet:
 *   · no Government Checklist under Solutions (rows 111-116 list four solutions)
 *   · Performance Summary (SW_GO) where Partner has Growth Opportunities
 *   · My Classes instead of My Schools
 *   · Insights holds School Dashboard, not My Dashboard / National Dashboard
 *   · Settings has three tabs — no Billing and Payments (rows 144-146)
 */

/** Rows the sheet removes from the current School sidebar. Recorded, not silently dropped. */
export const SCHOOL_WORKSPACE_REMOVED = [
    { label: 'Your Programs > Assessments', reason: 'Remove — preview moves to Solutions > COS Children Assessment > Preview (row 112)' },
    { label: 'Data & Insights', reason: 'Moved under Insights' },
];

export const SCHOOL_WORKSPACE_NAV = [
    {
        code: 'SW_HO',
        /**
         * Tutorial step-1 anchor. REQUIRED — TutorialManager force-navigates to a
         * step's route until it finds the target, so losing this id traps the user
         * on Home and breaks every other sidebar item. See the same note on PW_HO.
         */
        id: 'sidebar-home-link',
        label: 'Home',
        icon: HomeIcon,
        path: '/my_workspace',
        solutions: [SOLUTION.P0, SOLUTION.P1, SOLUTION.LC],
        visibleWhen: () => true,
    },

    {
        code: 'SW_SO',
        label: 'Solutions',
        icon: CategoryIcon,
        // Parent navigates only when the drawer is collapsed; otherwise it expands.
        path: '/dq_pro/solutions',
        slideCode: 'CS-Solution-Overview',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        /**
         * Common Section — the SAME pages the Partner workspace shows, so these
         * reuse the existing routes rather than adding school-specific copies.
         *
         * Each solution's Overview / Preview are Level 3 sidebar rows: the sheet
         * puts them in the Level 3 Code and Level 3 Name columns and keeps Tab
         * Code / Tab Name for the Settings page.
         *
         * COS Readiness Solution has an Overview and no Preview (row 112).
         */
        children: [
            {
                code: 'SW_SO_OV', label: 'Overview', path: '/dq_pro/solutions/overview',
                slideCode: 'CS-Solution-Overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
            },
            {
                code: 'SW_SO_P1', label: 'COS Readiness Solution', path: '/dq_pro/solutions/readiness',
                slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'SW_SO_P1_OV', label: 'Overview', path: '/dq_pro/solutions/readiness/overview',
                        slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'SW_SO_P0', label: 'COS Children Assessment', path: '/dq_pro/solutions/children-assessment',
                slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'SW_SO_P0_OV', label: 'Overview', path: '/dq_pro/solutions/children-assessment/overview',
                        slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        code: 'SW_SO_P0_PV', label: 'Preview', path: '/dq_pro/solutions/children-assessment/preview',
                        // The path above redirects to the COS catalogue preview page.
                        matchPrefixes: [COS_PREVIEW_PATH],
                        slideCode: 'CS-Solution-P0-preview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'SW_SO_LC', label: 'LearnCreator', path: '/dq_pro/solutions/learncreator',
                slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'SW_SO_LC_OV', label: 'Overview', path: '/dq_pro/solutions/learncreator/overview',
                        slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        code: 'SW_SO_LC_PV', label: 'Preview', path: '/dq_pro/solutions/learncreator/preview',
                        slideCode: 'CS-Solution-LC-preview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
        ],
    },

    {
        code: 'SW_GO',
        label: 'Performance Summary',
        icon: TrendingUpIcon,
        path: '/dq_pro/performance-summary',
        // Sheet row 117 codes this SNW_GO_KS — an SNW prefix inside the SW block,
        // i.e. a copy/paste slip. The Level 1 code SW_GO is the one used here.
        slideCode: 'SW-{P0|P1A|P1B|LC}-Performance Summary',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
    },

    {
        code: 'SW_MC',
        /** Tutorial step-4 anchor, and the Home page's FloatingNudge anchor. */
        id: 'sidebar-classes-link',
        label: 'My Classes',
        icon: PeopleIcon,
        path: '/dq_pro/classes',
        slideCode: 'SW-ALL-My Classes-Main',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        note: 'Was "Your Classes". Page actions (AddAccounts, Publish, AssignLicense, LinkPrograms) are unchanged — they need backend.',
        /*
         * Expands, like My Programs — the path above is only used when the
         * drawer is collapsed and there is nothing to expand into.
         *
         * The queue belongs here rather than at the top level because it is
         * the same audience and the same scope: a school reviewing what its
         * OWN learners asked to correct, class by class.
         */
        children: [
            {
                code: 'SW_MC_CL',
                label: 'Classes',
                path: '/dq_pro/classes',
                solutions: [SOLUTION.ALL],
                visibleWhen: () => true,
            },
            {
                code: 'SW_MC_PCR',
                label: 'Profile Requests',
                path: '/dq_pro/profile-change-requests',
                // Every school: any of them can receive a request, and the page
                // says the queue is empty rather than vanishing until the first
                // one arrives.
                solutions: [SOLUTION.ALL],
                visibleWhen: () => true,
            },
        ],
    },

    {
        code: 'SW_MP',
        label: 'My Programs',
        icon: EmojiObjectsIcon,
        // Parent expands; the path is only used when the drawer is collapsed.
        path: '/dq_pro/learning/program/list',
        slideCode: 'SW-{P1A|P1B|LC}-MyPrograms',
        solutions: [SOLUTION.P1A, SOLUTION.P1B, SOLUTION.LC],
        /**
         * Hidden unless the school can actually reach something inside. Carried
         * over verbatim so the restructure does not change who sees this.
         */
        visibleWhen: (ctx) => ctx.hasAnyProgramAccess,
        children: [
            {
                code: 'SW_MP_CR',
                label: 'Create',
                path: '/dq_pro/create',
                solutions: [SOLUTION.P1A, SOLUTION.P1B, SOLUTION.LC],
                // The create permission, not merely view — and never for level 22
                // regardless of that permission: program creation for them goes
                // through the LearnCreator menu item (SW_LC below), not here.
                visibleWhen: (ctx) => ctx.canCreateLearningPrograms
                    && Number(ctx.authUser?.access_level) !== USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE,
            },
            {
                code: 'SW_MP_LP',
                /** Tutorial step-2 anchor — level 22 is included in that step. */
                id: 'sidebar-products-link',
                label: 'Learning Programs',
                path: '/dq_pro/learning/program/list',
                solutions: [SOLUTION.P1A, SOLUTION.P1B, SOLUTION.LC],
                visibleWhen: (ctx) => ctx.hasLearningPrograms && ctx.canViewLearningPrograms,
            },
        ],
    },

    {
        code: 'SW_LC',
        label: 'LearnCreator',
        icon: EditIcon,
        // The LearnCreator landing page, which every entry point opens; typing
        // a goal there starts the builder (bug sheet Ian-LC-30Sep-1).
        path: LEARNCREATOR_START_PATH,
        slideCode: 'SW-P1B, LC-LearnCreator',
        solutions: [SOLUTION.P1B, SOLUTION.LC],
        /*
         * LearnCreator shows when the partner has ASSIGNED it to this workspace —
         * the partner's "Link Products" > Assign LearnCreator flow turns on the
         * org's learning_program `create` permission and the user's
         * create_learning_program capability. That assignment is what the sheet
         * means by P1B (teacher-created programs). Same rule as Home's Step 4
         * and lcActivated in ManageClasses.
         *
         * `enabled` alone is not that signal: createClient seeds every school
         * with learning_program enabled (view/link only) so it can see the
         * programs its partner delivers — reading it put the row on P1A too.
         */
        visibleWhen: (ctx) => ctx.canCreateLearningPrograms,
    },

    {
        code: 'SW_IN',
        label: 'Insights',
        icon: BarChartIcon,
        path: (ctx) => ctx.insightsPath,
        solutions: [SOLUTION.ALL],
        // Gated on the dashboard feature, exactly as before.
        visibleWhen: (ctx) => isFeatureEnabled(ctx.authUser, FEATURE_KEY.SCHOOL_DASHBOARD),
        /**
         * School Dashboard, then one entry per class.
         *
         * The sheet gives Insights two level-2 rows: SW_IN_SD "School Dashboard"
         * and SW_IN_CD "Class Dashboard", whose Level 3 Name is literally
         * "Class 1, 2, 3, ..." — one entry per class, not one link. That is
         * forced by the page itself: a class dashboard is scoped through a
         * `section_code` query param, and without one the API answers "Invalid
         * section code or access denied".
         *
         * So Class Dashboard is a PARENT whose children are built from the live
         * class list, which PartialMainList puts on the context as `classRows`.
         * WorkspaceNav already resolves `children` through `val()`, so a function
         * is a first-class value here and no component change was needed.
         *
         * A school with no classes yet shows no Class Dashboard row at all —
         * WorkspaceNav hides a parent whose children all filter out, which is the
         * right answer for "you have nothing to look at yet".
         */
        children: [
            {
                code: 'SW_IN_SD',
                label: 'School Dashboard',
                path: '/dq_pro/school-dashboard/overview',
                slideCode: 'SW-ALL-Insights-School Dashboard',
                solutions: [SOLUTION.ALL],
                visibleWhen: (ctx) => isFeatureEnabled(ctx.authUser, FEATURE_KEY.SCHOOL_DASHBOARD),
            },
            {
                code: 'SW_IN_CD',
                label: 'Class Dashboard',
                slideCode: 'SW-ALL-Insights-Class Dashboard',
                solutions: [SOLUTION.ALL],
                visibleWhen: (ctx) => isFeatureEnabled(ctx.authUser, FEATURE_KEY.CLASS_DASHBOARD),
                children: (ctx) => {
                    const rows = ctx.classRows || [];
                    if (!rows.length) {
                        return [1, 2].map((n) => ({
                            code: `SW_IN_CD_PLACEHOLDER_${n}`,
                            label: `Class ${n}`,
                            disabled: true,
                            solutions: [SOLUTION.ALL],
                            visibleWhen: () => true,
                        }));
                    }
                    return rows.map((c) => ({
                        code: `SW_IN_CD_${c.section_code}`,
                        label: c.section_name || c.section_code,
                        path: `/dq_pro/class-dashboard/executive-overview?section_code=${encodeURIComponent(c.section_code)}`,
                        solutions: [SOLUTION.ALL],
                        visibleWhen: () => true,
                    }));
                },
            },
        ],
    },

    /*
     * Support — added by the SNW, SW Parental Consent Flow (5 Oct 2026, slides
     * 7, 23 and 36): the school's Support page carries the Parental Consent
     * card. Teacher still has none.
     */
    {
        code: 'SW_SP',
        label: 'Support',
        icon: HelpOutlineIcon,
        path: '/support',
        slideCode: 'SW-Support',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
    },

    /*
     * MOVED: "Account settings" → Profile Icon > Settings, same as Partner.
     *
     * School's Settings has THREE tabs — Account, Security, Advanced Settings
     * (rows 144-146). There is no Billing and Payments tab for a school.
     */
];
